import React, { useState } from "react";

import {
  LAYER_SPECS,
  defaultControlValue,
  formatControlValue,
  resolveControlBounds,
  resolveControlOptions,
} from "./layerSpecs";

/**
 * Rendu d'un controle du registre (layerSpecs.js).
 *
 * UN SEUL rendu pour DEUX surfaces : le composer de vue du builder, ou
 * l'auteur fabrique la vue, et le panneau de l'animateur, ou il l'ajuste en
 * seance. C'est la contrepartie directe du registre — declarer un reglage
 * suffit a le faire apparaitre aux deux endroits, et les deux ne peuvent pas
 * proposer des choses differentes.
 *
 * Un controle dont le datapack ne peut pas peupler les options ne s'affiche
 * PAS : proposer un selecteur vide, ou un curseur sans effet, revient a
 * promettre quelque chose que la couche ne sait pas faire.
 */
export function LayerControl({ control, value, onChange, catalog }) {
  const help = control.help ? (
    <span className="terrain3d-control-help" title={control.help} aria-label={control.help}>?</span>
  ) : null;

  if (control.type === "toggle") {
    return (
      <label className="terrain3d-switchrow">
        <input type="checkbox" checked={value !== false} onChange={(e) => onChange(e.target.checked)} />
        <span>{control.label}{help}</span>
      </label>
    );
  }

  if (control.type === "select" || control.type === "segmented") {
    const options = resolveControlOptions(control, catalog);
    if (!options.length) return null;

    // Segmente sous 5 choix, liste deroulante au-dela : comparer actuel /
    // 2030 / 2050 est un geste de comparaison, il doit tenir en un clic ; une
    // liste de 29 indicateurs, non.
    if (control.type === "segmented" && options.length <= 5) {
      return (
        <div className="terrain3d-controlrow">
          <span className="terrain3d-control-label">{control.label}{help}</span>
          <div className="terrain3d-segmented" role="group" aria-label={control.label}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`terrain3d-segment${option.value === value ? " is-active" : ""}`}
                onClick={() => onChange(option.value)}
                aria-pressed={option.value === value}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // Les indicateurs portent un `group` (Secheresse, Agriculture...) : le
    // conserver dans le selecteur est ce qui rend une liste de 29 entrees
    // parcourable.
    const groups = [];
    options.forEach((option) => {
      const name = option.group || "";
      const bucket = groups.find((g) => g.name === name);
      if (bucket) bucket.options.push(option);
      else groups.push({ name, options: [option] });
    });
    const hasGroups = groups.length > 1 || (groups[0] && groups[0].name);

    return (
      <label className="terrain3d-controlrow">
        <span className="terrain3d-control-label">{control.label}{help}</span>
        <select
          className="terrain3d-select"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          aria-label={control.label}
        >
          {hasGroups
            ? groups.map((group) => (
                <optgroup key={group.name || "—"} label={group.name || "Autres"}>
                  {group.options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </optgroup>
              ))
            : options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
        </select>
      </label>
    );
  }

  if (control.type === "range") {
    const { min, max } = resolveControlBounds(control, catalog);
    if (!(max > min)) return null;

    // Echelle logarithmique quand la grandeur couvre plusieurs ordres de
    // grandeur (longueur des cours d'eau : de 5 km a plusieurs centaines) —
    // en lineaire, tout le reglage utile se joue dans les premiers pixels.
    const isLog = control.scale === "log";
    const toSlider = (v) => (isLog ? Math.log10(Math.max(v || min, 1)) : v);
    const fromSlider = (v) => (isLog ? Math.pow(10, v) : v);

    return (
      <label className="terrain3d-slider">
        <span>
          {control.label}{help} <strong>{formatControlValue(control, value)}</strong>
        </span>
        <input
          type="range"
          min={toSlider(min)}
          max={toSlider(max)}
          step={isLog ? 0.01 : (control.step ?? 0.05)}
          value={toSlider(value ?? min)}
          onChange={(e) => onChange(fromSlider(parseFloat(e.target.value)))}
        />
      </label>
    );
  }

  if (control.type === "exclusions") {
    return <ExclusionControl control={control} value={value} onChange={onChange} catalog={catalog} help={help} />;
  }

  return null;
}

/**
 * Liste d'exclusions.
 *
 * CE QUI A CHANGE. La premiere version affichait TOUTES les options en puces
 * a basculer. Correct pour huit usages de prelevement, intenable pour soixante
 * communes : la liste noyait le reste des reglages et l'auteur devait la
 * parcourir a l'oeil pour retrouver une ville.
 *
 * L'exclusion etant par nature RARE — on masque deux ou trois elements, pas
 * cinquante — le controle montre desormais l'etat (ce qui est masque) et non
 * le catalogue. Ajouter passe par une recherche, qui ne deroule des
 * propositions qu'une fois quelque chose tape.
 */
function ExclusionControl({ control, value, onChange, catalog, help }) {
  const [query, setQuery] = useState("");
  const options = resolveControlOptions({ options: control.optionsKey }, catalog);
  if (!options.length) return null;

  const hidden = value || [];
  const hiddenSet = new Set(hidden);
  const labelFor = (id) => options.find((o) => o.value === id)?.label || id;

  const needle = query.trim().toLowerCase();
  const suggestions = needle
    ? options
        .filter((o) => !hiddenSet.has(o.value) && o.label.toLowerCase().includes(needle))
        .slice(0, 6)
    : [];

  const add = (id) => {
    onChange([...hidden, id]);
    setQuery("");
  };

  return (
    <div className="terrain3d-controlrow">
      <div className="terrain3d-sectionhead">
        <span className="terrain3d-control-label">{control.label}{help}</span>
        {hidden.length > 0 && (
          <button type="button" className="terrain3d-textbtn" onClick={() => onChange([])}>
            Tout rétablir
          </button>
        )}
      </div>

      {hidden.length > 0 && (
        <div className="terrain3d-chiplist">
          {hidden.map((id) => (
            <button
              key={id}
              type="button"
              className="terrain3d-chip terrain3d-chip--removable"
              onClick={() => onChange(hidden.filter((h) => h !== id))}
              title="Réafficher"
            >
              <span className="terrain3d-chip-label">{labelFor(id)}</span>
              <span className="terrain3d-chip-remove" aria-hidden="true">✕</span>
            </button>
          ))}
        </div>
      )}

      <div className="terrain3d-exclusion-search">
        <input
          type="search"
          className="terrain3d-textinput"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Masquer… (${options.length} disponibles)`}
          aria-label={control.label}
        />
        {suggestions.length > 0 && (
          <div className="terrain3d-exclusion-results">
            {suggestions.map((option) => (
              <button
                key={option.value}
                type="button"
                className="terrain3d-exclusion-result"
                onClick={() => add(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Tous les reglages d'une couche, dans l'ordre du registre.
 *
 * Chaque controle affiche sa valeur EFFECTIVE : celle de l'appelant si elle
 * existe, sinon le defaut du registre resolu contre le catalogue. Sans ce
 * repli, le builder — qui ne detient que les reglages epars de l'auteur —
 * affichait un tiret et un curseur cale a gauche tant que rien n'avait ete
 * touche, ce qui donnait a lire l'inverse de ce que la carte montrait.
 *
 * L'ECRITURE, elle, reste eparse : seul ce que l'auteur deplace est remonte.
 * C'est la condition pour que `pruneLayerSettings` puisse ensuite ne garder
 * que de vraies decisions.
 */
export function LayerControlGroup({ layerId, settings, onChange, catalog, exclude = [] }) {
  const spec = LAYER_SPECS[layerId];
  if (!spec) return null;
  const controls = spec.controls.filter((control) => !exclude.includes(control.id));
  if (!controls.length) return null;

  return (
    <>
      {controls.map((control) => {
        const value = settings?.[control.id];
        return (
          <LayerControl
            key={control.id}
            control={control}
            value={value === undefined ? defaultControlValue(control, catalog) : value}
            onChange={(next) => onChange(control.id, next)}
            catalog={catalog}
          />
        );
      })}
    </>
  );
}
